import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AccessibilityConfig {
  screenReaderMode: boolean;
  highContrastMode: boolean;
  reducedMotion: boolean;
  keyboardNavigation: boolean;
  fontSize: 'normal' | 'large' | 'extra-large';
  announcements: boolean;
  visualIndicators: boolean;
  focusIndicators: boolean;
}

interface AccessibilityContextType {
  config: AccessibilityConfig;
  updateConfig: (updates: Partial<AccessibilityConfig>) => void;
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  getFontSizeClass: () => string;
  getContrastClass: (baseClass: string) => string;
}

const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

// ARIA live region for announcements
let announcementContainer: HTMLDivElement | null = null;

export const AccessibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AccessibilityConfig>(() => {
    const saved = localStorage.getItem('wordrush_accessibility');
    if (saved) {
      return JSON.parse(saved);
    }
    
    // Check system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    
    return {
      screenReaderMode: false,
      highContrastMode: prefersHighContrast,
      reducedMotion: prefersReducedMotion,
      keyboardNavigation: true,
      fontSize: 'normal',
      announcements: true,
      visualIndicators: true,
      focusIndicators: true
    };
  });

  // Create ARIA live region on mount
  useEffect(() => {
    if (!announcementContainer) {
      announcementContainer = document.createElement('div');
      announcementContainer.setAttribute('aria-live', 'polite');
      announcementContainer.setAttribute('aria-atomic', 'true');
      announcementContainer.className = 'sr-only';
      document.body.appendChild(announcementContainer);
    }
    
    return () => {
      if (announcementContainer && document.body.contains(announcementContainer)) {
        document.body.removeChild(announcementContainer);
        announcementContainer = null;
      }
    };
  }, []);

  // Apply global accessibility styles
  useEffect(() => {
    const root = document.documentElement;
    
    // High contrast mode
    if (config.highContrastMode) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Reduced motion
    if (config.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
    // Font size
    root.classList.remove('text-large', 'text-extra-large');
    if (config.fontSize === 'large') {
      root.classList.add('text-large');
    } else if (config.fontSize === 'extra-large') {
      root.classList.add('text-extra-large');
    }
    
    // Focus indicators
    if (config.focusIndicators) {
      root.classList.add('focus-visible');
    } else {
      root.classList.remove('focus-visible');
    }
  }, [config]);

  // Save config to localStorage
  useEffect(() => {
    localStorage.setItem('wordrush_accessibility', JSON.stringify(config));
  }, [config]);

  const updateConfig = (updates: Partial<AccessibilityConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (!config.announcements || !announcementContainer) return;
    
    // Update aria-live attribute if needed
    announcementContainer.setAttribute('aria-live', priority);
    
    // Clear previous announcement
    announcementContainer.textContent = '';
    
    // Set new announcement after a brief delay
    setTimeout(() => {
      if (announcementContainer) {
        announcementContainer.textContent = message;
      }
    }, 100);
  };

  const getFontSizeClass = () => {
    switch (config.fontSize) {
      case 'large':
        return 'text-lg';
      case 'extra-large':
        return 'text-xl';
      default:
        return '';
    }
  };

  const getContrastClass = (baseClass: string) => {
    if (!config.highContrastMode) return baseClass;
    
    // Map common colors to high contrast versions
    if (baseClass.includes('gray-')) {
      return baseClass.replace(/gray-\d{3}/, 'black');
    }
    if (baseClass.includes('blue-')) {
      return baseClass.replace(/blue-\d{3}/, 'blue-900');
    }
    if (baseClass.includes('green-')) {
      return baseClass.replace(/green-\d{3}/, 'green-900');
    }
    if (baseClass.includes('red-')) {
      return baseClass.replace(/red-\d{3}/, 'red-900');
    }
    
    return baseClass;
  };

  return (
    <AccessibilityContext.Provider value={{
      config,
      updateConfig,
      announce,
      getFontSizeClass,
      getContrastClass
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

// Keyboard navigation hook
export const useKeyboardNavigation = (enabled: boolean = true) => {
  useEffect(() => {
    if (!enabled) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Tab navigation enhancement
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-nav');
      }
      
      // Skip to main content (Alt + S)
      if (e.altKey && e.key === 's') {
        e.preventDefault();
        const main = document.querySelector('main') || document.querySelector('[role="main"]');
        if (main instanceof HTMLElement) {
          main.focus();
          main.scrollIntoView({ behavior: 'smooth' });
        }
      }
      
      // Navigate sections (Alt + Arrow keys)
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const sections = Array.from(document.querySelectorAll('[role="region"], section'));
        const currentIndex = sections.findIndex(s => s.contains(document.activeElement));
        
        let nextIndex = currentIndex;
        if (e.key === 'ArrowUp') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : sections.length - 1;
        } else {
          nextIndex = currentIndex < sections.length - 1 ? currentIndex + 1 : 0;
        }
        
        const next = sections[nextIndex];
        if (next instanceof HTMLElement) {
          next.focus();
          next.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    
    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-nav');
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [enabled]);
};

// Screen reader announcements for game events
export const useGameAnnouncements = () => {
  const { announce } = useAccessibility();
  
  return {
    announceRoundStart: (letter: string) => {
      announce(`Round started. The letter is ${letter}`, 'assertive');
    },
    announceTimeWarning: (seconds: number) => {
      announce(`${seconds} seconds remaining`, 'polite');
    },
    announceSubmission: () => {
      announce('Answers submitted successfully', 'polite');
    },
    announceScore: (score: number) => {
      announce(`You scored ${score} points this round`, 'polite');
    },
    announceWinner: (name: string) => {
      announce(`${name} wins the game!`, 'assertive');
    },
    announcePlayerJoined: (name: string) => {
      announce(`${name} joined the game`, 'polite');
    },
    announcePlayerLeft: (name: string) => {
      announce(`${name} left the game`, 'polite');
    }
  };
};